import { EventType } from "../../enums";

// A service's "buffer after reservation" keeps the employee busy after the client's part of
// the reservation ends. It never runs past the end of the employee's working day or into a
// blocked time (a break), so the last slot before either stays bookable. Mirrors
// ServiceBufferUtility on the backend.

const MINUTES_IN_DAY = 24 * 60;

// A block of working hours, in minutes from midnight.
export interface IWorkingShiftMinutes {
  start: number;
  end: number;
}

// A blocked time (a break) of the employee, in minutes from midnight.
export interface IBlockedIntervalMinutes {
  start: number;
  end: number;
}

// The times of an employee's booked slot as the API sends them. `busyUntil` is when the
// employee is free again: the end plus that reservation's own buffer, already cut by the
// backend. A slot without it has no buffer.
interface IBookedSlotTimes {
  startTime: string;
  endTime: string;
  busyUntil?: string;
}

const timeToMinutes = (time: string): number => {
  const [hour, minute] = time.split(":").map(Number);
  return hour * 60 + minute;
};

// Wall-clock minutes of `time` on `day`, cut to that day: 0 before it, 1440 after it.
const minutesOnDay = (time: Date, day: Date): number => {
  const dayStart = new Date(day.getFullYear(), day.getMonth(), day.getDate());
  const nextDayStart = new Date(day.getFullYear(), day.getMonth(), day.getDate() + 1);

  if (time <= dayStart) return 0;
  if (time >= nextDayStart) return MINUTES_IN_DAY;
  return time.getHours() * 60 + time.getMinutes();
};

export const toWorkingShifts = (
  workingHours: { startTime: string; endTime: string }[],
): IWorkingShiftMinutes[] =>
  workingHours.map((wh) => ({
    start: timeToMinutes(wh.startTime),
    end: timeToMinutes(wh.endTime),
  }));

// The employee's blocked times that fall on `date`, in minutes of that day; one running over
// several days is cut to that day.
export const toBlockedIntervals = (
  bookedSlots: (IBookedSlotTimes & { eventType: number })[],
  date: Date,
): IBlockedIntervalMinutes[] =>
  bookedSlots
    .filter((slot) => slot.eventType === EventType.BlockedTime)
    .map((slot) => ({
      start: minutesOnDay(new Date(slot.startTime), date),
      end: minutesOnDay(new Date(slot.endTime), date),
    }))
    .filter((blocked) => blocked.start < blocked.end);

export const getBusyUntil = (slot: IBookedSlotTimes): Date =>
  new Date(slot.busyUntil || slot.endTime);

// The minutes a booked slot keeps the employee busy after the reservation itself ends.
export const getBookedBufferMinutes = (slot: IBookedSlotTimes): number =>
  Math.max(
    0,
    Math.round(
      (getBusyUntil(slot).getTime() - new Date(slot.endTime).getTime()) / 60000,
    ),
  );

// A booked slot on `day`, in minutes of that day: from its start until the employee is free
// again (its own buffer included), cut to the day.
export const toBusyInterval = (
  slot: IBookedSlotTimes,
  day: Date,
): { start: number; end: number } => ({
  start: minutesOnDay(new Date(slot.startTime), day),
  end: minutesOnDay(getBusyUntil(slot), day),
});

const withinWorkingDay = (
  endMinute: number,
  bufferMinutes: number | null | undefined,
  shifts: IWorkingShiftMinutes[],
): number => {
  const buffer = Math.max(0, Number(bufferMinutes) || 0);
  if (buffer === 0) return 0;
  if (shifts.length === 0) return buffer;

  const ordered = [...shifts].sort((a, b) => a.start - b.start);
  const endOfDay = Math.max(...ordered.map((s) => s.end));
  if (endMinute >= endOfDay) return 0;

  let stretchEnd: number | null = null;
  for (const s of ordered) {
    const containsEnd = s.start < endMinute && endMinute <= s.end;
    const continuesStretch = stretchEnd !== null && s.start <= stretchEnd;

    if ((containsEnd || continuesStretch) && s.end > (stretchEnd ?? 0)) {
      stretchEnd = s.end;
    }
  }

  if (stretchEnd === null) return buffer;
  return Math.min(buffer, stretchEnd - endMinute);
};

// The minutes of the buffer that apply after a reservation ending at `endMinute`:
// - ends inside working hours: cut at the end of that stretch of work (back-to-back shifts
//   count as one stretch);
// - ends after the last working hour of the day: no buffer;
// - ends before or between shifts, or on a day without working hours: the whole buffer;
// - a blocked time starting inside the buffer cuts it where the blocked time starts, like the
//   end of a shift; a reservation ending inside one (or right at its start) gets no buffer.
export const getEffectiveBufferMinutes = (
  endMinute: number,
  bufferMinutes: number | null | undefined,
  shifts: IWorkingShiftMinutes[],
  blockedIntervals: IBlockedIntervalMinutes[] = [],
): number => {
  let minutes = withinWorkingDay(endMinute, bufferMinutes, shifts);
  if (minutes === 0) return 0;

  for (const blocked of blockedIntervals) {
    if (blocked.start <= endMinute && endMinute < blocked.end) return 0;

    if (blocked.start >= endMinute && blocked.start < endMinute + minutes) {
      minutes = blocked.start - endMinute;
    }
  }

  return minutes;
};
